const DEFAULT_DB_NAME = 'solvit-ui';
const DEFAULT_STORE_NAME = 'drawerWidths';
const DEFAULT_DB_VERSION = 1;

function openDrawerWidthDb({ dbName, storeName, dbVersion }) {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('indexedDB is not available'));
      return;
    }
    const request = indexedDB.open(dbName, dbVersion);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = event => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName);
      }
    };
  });
}

/**
 * Drawer width IndexedDB storage adapter
 *
 * @param {object} [options]
 * @param {string} [options.dbName]
 * @param {string} [options.storeName]
 * @param {number} [options.dbVersion]
 * @returns {{ id: string, get: (key: string) => Promise<number|null>, set: (key: string, width: number) => Promise<void> }}
 */
export function createIndexedDbDrawerWidthAdapter(options = {}) {
  const dbName = options.dbName ?? DEFAULT_DB_NAME;
  const storeName = options.storeName ?? DEFAULT_STORE_NAME;
  const dbVersion = options.dbVersion ?? DEFAULT_DB_VERSION;

  let dbPromise = null;
  const getDb = () => {
    if (!dbPromise) {
      dbPromise = openDrawerWidthDb({ dbName, storeName, dbVersion });
    }
    return dbPromise;
  };

  const normalizeRecord = raw => {
    if (raw == null) {
      return null;
    }
    if (typeof raw === 'number') {
      return raw;
    }
    if (typeof raw.width === 'number') {
      return raw.width;
    }
    return null;
  };

  return {
    id: 'indexeddb-drawer-width',

    async get(key) {
      const db = await getDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readonly');
        const store = tx.objectStore(storeName);
        const request = store.get(key);
        request.onsuccess = () => resolve(normalizeRecord(request.result));
        request.onerror = () => reject(request.error);
      });
    },

    async set(key, width) {
      const db = await getDb();
      return new Promise((resolve, reject) => {
        const tx = db.transaction(storeName, 'readwrite');
        const store = tx.objectStore(storeName);
        const request = store.put(
          {
            width,
            updatedAt: new Date().toISOString()
          },
          key
        );
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });
    }
  };
}
